(() => {
  if (window.gcoAccessibilityInstalled) return;
  window.gcoAccessibilityInstalled = true;
  const focusable = 'a[href],button,input,select,textarea,[tabindex],summary';
  const visible = el => el.isConnected && !el.closest('[hidden],[inert]') && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  const stops = root => [...root.querySelectorAll(focusable)].filter(el => el.tabIndex >= 0 && !el.matches(':disabled') && !el.closest('[aria-hidden="true"]') && visible(el));
  let active = null;
  let opener = null;
  let previousFocus = null;
  let isolated = new Map();
  let dialogAttributes = null;
  let closeFocused = false;
  let ownLabel = false;
  let returnTo = null;
  let lastInCart = null;
  let parked = false;
  const describe = el => ({tag: el.tagName, cls: typeof el.className === 'string' ? el.className : '', label: el.getAttribute('aria-label')});
  const sameAs = (el, ref) => el.tagName === ref.tag && (typeof el.className === 'string' ? el.className : '') === ref.cls && el.getAttribute('aria-label') === ref.label;
  const matches = (root, ref) => [...root.getElementsByTagName(ref.tag)].filter(el => sameAs(el, ref));
  const findAgain = cart => {
    if (!lastInCart) return null;
    const all = matches(cart, lastInCart);
    const el = all[lastInCart.index] || all[0];
    return el && stops(cart).includes(el) ? el : null;
  };
  const nameCart = cart => {
    const title = ownLabel && cart.querySelector('.upcart-header-text');
    const text = title && title.textContent.trim();
    if (text && cart.getAttribute('aria-label') !== text) cart.setAttribute('aria-label', text);
  };
  const restoreBackground = () => {
    for (const [el, wasInert] of isolated) {
      if (!wasInert) el.removeAttribute('inert');
    }
    isolated.clear();
  };
  const isolate = root => {
    let branch = root;
    while (branch.parentElement && branch !== document.body) {
      for (const sibling of branch.parentElement.children) {
        if (sibling === branch || /^(SCRIPT|STYLE|LINK|TEMPLATE)$/.test(sibling.tagName)) continue;
        if (!isolated.has(sibling)) isolated.set(sibling, sibling.hasAttribute('inert'));
        if (!sibling.hasAttribute('inert')) sibling.setAttribute('inert', '');
      }
      branch = branch.parentElement;
    }
  };
  const release = () => {
    if (!active) return;
    const cart = active;
    const restoreFocus = cart.contains(document.activeElement) || document.activeElement === document.body;
    for (const [name, value] of dialogAttributes) {
      if (value === null) cart.removeAttribute(name);
      else cart.setAttribute(name, value);
    }
    active = null;
    lastInCart = null;
    parked = false;
    restoreBackground();
    returnTo = null;
    if (restoreFocus) {
      // The theme disables Add to Cart while it adds, which drops focus to body; fall back to the last control focused before the cart.
      returnTo = [opener, previousFocus].find(el => el && el !== document.body && visible(el)) || null;
      if (returnTo) returnTo.focus();
      else if (cart.contains(document.activeElement)) document.activeElement.blur();
    }
    opener = null;
  };
  const sync = () => {
    const root = document.querySelector('#UpcartPopup.upcartPopupShow');
    const cart = root && root.querySelector('#CartPopup');
    if (active && (cart !== active || !active.isConnected)) release();
    if (!cart || !visible(cart)) {
      if (active) release();
      return;
    }
    if (!active) {
      active = cart;
      opener = cart.contains(document.activeElement) || document.activeElement === document.body ? previousFocus : document.activeElement;
      dialogAttributes = new Map(['role', 'aria-modal', 'aria-label'].map(name => [name, cart.getAttribute(name)]));
      if (cart.getAttribute('role') !== 'dialog') cart.setAttribute('role', 'dialog');
      if (cart.getAttribute('aria-modal') !== 'true') cart.setAttribute('aria-modal', 'true');
      ownLabel = !cart.hasAttribute('aria-label') && !cart.hasAttribute('aria-labelledby');
      nameCart(cart);
      isolate(cart);
      const close = cart.querySelector('.upcart-header-close-button');
      closeFocused = !!close;
      (close || stops(cart)[0] || cart).focus();
    } else {
      isolate(cart);
      nameCart(cart);
      const close = cart.querySelector('.upcart-header-close-button');
      if (!cart.contains(document.activeElement)) {
        // UpCart disables a line's controls while its quantity updates, then redraws the line, which drops focus to the page.
        // Return to the same control if it is usable, otherwise hold focus on the cart until it is back.
        const target = findAgain(cart);
        if (target) target.focus();
        else {
          parked = true;
          (cart.hasAttribute('tabindex') ? cart : stops(cart)[0] || cart).focus();
        }
      } else if (parked && document.activeElement === cart) {
        const target = findAgain(cart);
        if (target) {
          parked = false;
          target.focus();
        }
      } else if (!closeFocused && close && document.activeElement === cart) {
        // UpCart can draw its header after the drawer opens; move focus from the container to Close once it exists.
        closeFocused = true;
        close.focus();
      }
    }
  };
  // Fillout titles its frame with the bare form id, which a screen reader reads out as letters.
  const frameTitles = {bExR5Hy1DTus: 'Start a return, exchange or claim'};
  const labelFrames = () => {
    for (const frame of document.querySelectorAll('iframe[src*="fillout.com"]')) {
      const host = frame.closest('[data-fillout-id]');
      const id = (host && host.getAttribute('data-fillout-id')) || ((frame.getAttribute('src') || '').match(/\/t\/([^/?#]+)/) || [])[1];
      const title = frame.getAttribute('title');
      if (frameTitles[id] && (!title || title === id)) frame.setAttribute('title', frameTitles[id]);
    }
  };
  // Loox's review pop-up close link has no text or label.
  const nameAppControls = () => {
    for (const link of document.querySelectorAll('.loox-pn-close:not([aria-label])')) link.setAttribute('aria-label', 'Close');
  };
  // Webyze color swatches are <span role="button"> with no tabindex, so a keyboard cannot reach the color switcher.
  // Make each one focusable; Enter and Space press it the way a click does (keydown handler below).
  const swatches = '.webyzeProductColors > .swatchProductColor[role="button"]';
  const rememberedSwatch = () => {
    try { return JSON.parse(sessionStorage.getItem('gcoSwatchFocus')); } catch (e) { return null; }
  };
  const forgetSwatch = () => {
    try { sessionStorage.removeItem('gcoSwatchFocus'); } catch (e) {}
  };
  const fixSwatches = () => {
    for (const swatch of document.querySelectorAll(swatches + ':not([tabindex])')) swatch.tabIndex = 0;
    // A color change loads the other color's page; a keyboard user lands back on the color they chose, not the top of the page.
    // Webyze draws the row more than once, so the place is held until the shopper next presses a key or the pointer.
    const chosen = rememberedSwatch();
    if (!chosen) return;
    if (Date.now() - chosen.at > 30000) forgetSwatch();
    else if (location.pathname.endsWith('/' + chosen.handle)) {
      const swatch = [...document.querySelectorAll(swatches)].find(el => el.getAttribute('data-handle') === chosen.handle && visible(el));
      if (swatch && document.activeElement === document.body) swatch.focus();
    } else if (location.pathname !== chosen.from) forgetSwatch();
  };
  // The size guide (snippets/sizechart.liquid) is a CSS :target overlay that left focus on the page behind it. Opening it moves
  // focus to Close and Tab stays inside; Escape or Close shuts it without a reload and focus goes back to the Size Guide link.
  const guideLink = 'a[href="#sizepopup"]';
  let guideOpener = null;
  const openGuide = () => (location.hash === '#sizepopup' && document.getElementById('sizepopup')) || null;
  const showGuide = () => {
    const guide = openGuide();
    if (!guide) return;
    if (!guideOpener) guideOpener = [...document.querySelectorAll(guideLink)].find(visible) || null;
    const close = guide.querySelector('.close');
    if (!close) return;
    close.focus();
    // The browser finishes the jump to #sizepopup after hashchange and can move focus off Close; put it back.
    const hold = () => {
      if (openGuide() === guide && (document.activeElement === document.body || document.activeElement === guide)) close.focus();
    };
    requestAnimationFrame(() => requestAnimationFrame(hold));
    setTimeout(hold, 300);
  };
  const shutGuide = restore => {
    const to = guideOpener;
    guideOpener = null;
    // #sizeguide is no element: the overlay loses :target without a reload or a jump, and Back does not reopen it.
    location.replace('#sizeguide');
    if (restore && to && visible(to)) to.focus();
  };
  // Swym replaces each product card's wishlist label with a bare "Add to Wishlist"; keep the product name in it.
  const nameWishlists = () => {
    for (const button of document.querySelectorAll('article.productitem > .swym-button')) {
      const title = button.parentElement.querySelector('.productitem--title a');
      const name = title && title.textContent.trim();
      const label = button.getAttribute('aria-label') || 'Add to wishlist';
      if (name && !label.includes(name)) button.setAttribute('aria-label', label + ': ' + name);
    }
  };
  // Instafeed's post pop-up puts a decorative "Shop now" <button tabindex="-1"> inside each product link and names every
  // link "Open product". Keep the look (Instafeed styles it by class), drop the nested control, and name the link for its piece.
  const fixInstafeed = () => {
    for (const button of document.querySelectorAll('a button.tagged-buy-button')) {
      const label = document.createElement('span');
      label.className = button.className;
      if (button.hasAttribute('style')) label.setAttribute('style', button.getAttribute('style'));
      label.textContent = button.textContent;
      button.replaceWith(label);
    }
    for (const label of document.querySelectorAll('a > span.tagged-buy-button')) {
      const link = label.parentElement;
      const handle = ((link.getAttribute('href') || '').split('/products/')[1] || '').split(/[?#/]/)[0];
      if (!handle || link.hasAttribute('data-gco-named')) continue;
      link.setAttribute('data-gco-named', '');
      link.setAttribute('aria-label', 'Shop ' + decodeURIComponent(handle).split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
    }
  };
  // The home film loops on its own, so it gets a pause control (WCAG 2.2.2), and it stays on its still for visitors who ask
  // for reduced motion until they press play. The button's label and icon follow the film's own play and pause events.
  const reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  const setupFilms = () => {
    for (const toggle of document.querySelectorAll('[data-gco-film-toggle]:not([data-gco-ready])')) {
      const film = toggle.closest('.gco-ed-film');
      const video = film && film.querySelector('video');
      if (!video) continue;
      toggle.setAttribute('data-gco-ready', '');
      const show = playing => {
        film.classList.toggle('is-playing', playing);
        toggle.toggleAttribute('data-paused', !playing);
        toggle.setAttribute('aria-label', playing ? 'Pause film' : 'Play film');
      };
      video.addEventListener('play', () => show(true));
      video.addEventListener('pause', () => show(false));
      toggle.addEventListener('click', () => {
        if (video.paused) {
          const playing = video.play();
          show(true);
          if (playing && playing.catch) playing.catch(() => show(false));
        } else {
          video.pause();
          show(false);
        }
      });
      if (reduceMotion && reduceMotion.matches) {
        video.removeAttribute('autoplay');
        video.pause();
        show(false);
      } else {
        show(!video.paused || video.autoplay);
        // A browser that blocks autoplay never fires pause; show the play state rather than a pause button over a still.
        setTimeout(() => { if (video.paused) show(false); }, 2500);
      }
      toggle.hidden = false;
    }
  };
  const updateSkip = () => {
    const skip = document.querySelector('.skip-to-main');
    const target = [...document.querySelectorAll('[data-gco-content-start]')].find(visible);
    if (skip && target) skip.setAttribute('href', '#' + target.id);
    return target;
  };
  document.addEventListener('focusin', event => {
    const guide = openGuide();
    // Focus that leaves an open size guide (a click on the page behind it) closes the guide where the shopper went.
    if (guide && !guide.contains(event.target)) shutGuide(false);
    if (active) {
      if (!active.contains(event.target)) (stops(active)[0] || active).focus();
      else if (event.target !== active) {
        const ref = describe(event.target);
        lastInCart = {...ref, index: matches(active, ref).indexOf(event.target)};
        parked = false;
      }
    } else if (event.target.closest('#CartPopup')) {
      // UpCart focuses its own closed drawer after closing; send focus back to where the shopper was.
      if (returnTo && visible(returnTo)) returnTo.focus();
    } else {
      previousFocus = event.target;
      if (event.target !== returnTo) returnTo = null;
    }
  });
  document.addEventListener('click', event => {
    if (event.target.closest && event.target.closest(guideLink)) guideOpener = event.target.closest(guideLink);
    if (event.target.closest && event.target.closest('#sizepopup .close')) {
      event.preventDefault();
      shutGuide(true);
      return;
    }
    if (!event.target.closest('.skip-to-main')) return;
    const target = updateSkip();
    if (!target) return;
    event.preventDefault();
    target.focus();
    target.scrollIntoView({block: 'start'});
  });
  document.addEventListener('pointerdown', () => { if (rememberedSwatch()) forgetSwatch(); }, true);
  document.addEventListener('keydown', event => {
    if (rememberedSwatch()) forgetSwatch();
    if ((event.key === 'Enter' || event.key === ' ') && event.target.matches && event.target.matches(swatches)) {
      event.preventDefault();
      const handle = event.target.getAttribute('data-handle');
      if (handle && !event.target.classList.contains('currentSwatch')) {
        try { sessionStorage.setItem('gcoSwatchFocus', JSON.stringify({handle, from: location.pathname, at: Date.now()})); } catch (e) {}
      }
      event.target.click();
      return;
    }
    const guide = !active && openGuide();
    if (guide && event.key === 'Escape') {
      event.preventDefault();
      shutGuide(true);
      return;
    }
    if (guide && event.key === 'Tab') {
      const items = stops(guide);
      const at = items.indexOf(document.activeElement);
      if (!items.length) event.preventDefault();
      else if (event.shiftKey ? at <= 0 : at === -1 || at === items.length - 1) {
        event.preventDefault();
        (event.shiftKey ? items[items.length - 1] : items[0]).focus();
      }
      return;
    }
    if (!active) return;
    if (event.key === 'Escape') {
      const close = active.querySelector('.upcart-header-close-button');
      if (close) {
        event.preventDefault();
        close.click();
      }
    } else if ((event.key === ' ' || event.key === 'Enter') && event.target.matches('.upcart-header-close-button:not(button)')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.target.click();
    } else if (event.key === 'Tab') {
      const items = stops(active);
      const first = items[0];
      const last = items[items.length - 1];
      if (!first) {
        event.preventDefault();
        active.focus();
      } else if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
        event.preventDefault();
        first.focus();
      } else {
        // UpCart commits a quantity when its input loses focus and redraws the line before the browser can move focus,
        // which drops focus to the page. Point the redraw recovery at the control Tab is heading to, not back at the input.
        const next = items[items.indexOf(document.activeElement) + (event.shiftKey ? -1 : 1)];
        const ref = describe(next);
        lastInCart = {...ref, index: matches(active, ref).indexOf(next)};
      }
    }
  }, true);
  const start = () => {
    previousFocus = document.activeElement;
    updateSkip();
    sync();
    labelFrames();
    nameWishlists();
    nameAppControls();
    fixInstafeed();
    fixSwatches();
    setupFilms();
    new MutationObserver(records => {
      sync();
      labelFrames();
      nameAppControls();
      fixInstafeed();
      fixSwatches();
      if (records.some(r => r.type === 'childList' || r.target.classList.contains('swym-button'))) nameWishlists();
    }).observe(document.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'hidden', 'style', 'title', 'disabled', 'aria-label']});
    document.addEventListener('shopify:section:load', () => { updateSkip(); setupFilms(); });
    window.addEventListener('hashchange', () => {
      if (openGuide()) showGuide();
      else if (guideOpener) {
        // Closed some other way (Back, another link) while focus was in it.
        const to = guideOpener;
        guideOpener = null;
        const guide = document.getElementById('sizepopup');
        if ((document.activeElement === document.body || (guide && guide.contains(document.activeElement))) && visible(to)) to.focus();
      }
    });
    showGuide();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
